import React, { useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import useReactRouter from "use-react-router";
import { ORDER_BY_LINK } from "../../../graphql/orderLink/query";

/**
 * The buyer's own order, opened from a signed link in Messenger.
 *
 * NO INPUT, BY DESIGN. The buyer already proved who they are by tapping a link only they
 * received, so asking them to type a name or a phone would be theatre — and a phone box would
 * be worse than theatre: it would turn this into a lookup tool where anyone who knows a
 * customer's number can read their name, address and orders. The token is the credential.
 *
 * It shows the SAME checklist as the Messenger card, from the same fields, so a buyer can put
 * the two side by side and see them agree. That is the whole job: "is my order really done".
 *
 * MOBILE FIRST — every viewer arrives from the Facebook app on a phone.
 */

const FIELD_LABEL = {
  phone: "ເບີໂທ",
  address: "ທີ່ຢູ່",
  carrier: "ຂົນສົ່ງ",
  payment: "ຈ່າຍເງິນ",
};

/** Said plainly, because the buyer is deciding whether to message the shop again. */
const REASON_TEXT = {
  EXPIRED: "ລິ້ງນີ້ໝົດອາຍຸແລ້ວ ກະລຸນາກັບໄປທີ່ Messenger ແລ້ວກົດລິ້ງໃໝ່",
  BAD_SIGNATURE: "ລິ້ງນີ້ບໍ່ຖືກຕ້ອງ ກະລຸນາກັບໄປທີ່ Messenger ແລ້ວກົດລິ້ງໃໝ່",
  MALFORMED: "ລິ້ງນີ້ບໍ່ຖືກຕ້ອງ ກະລຸນາກັບໄປທີ່ Messenger ແລ້ວກົດລິ້ງໃໝ່",
  NOT_FOUND: "ບໍ່ພົບອໍເດີ້ຂອງລິ້ງນີ້",
};

const money = (n, cur) =>
  `${Number(n || 0).toLocaleString("en-US")} ${cur === "LAK" || !cur ? "ກີບ" : cur}`;

export default function MyOrder() {
  const { match, location } = useReactRouter();
  const token = match?.params?.token || "";
  const params = new URLSearchParams(location?.search || "");
  const sig = params.get("sig") || "";
  const e = params.get("e") || "";

  const [load, { data, loading, error }] = useLazyQuery(ORDER_BY_LINK, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (token && sig && e) load({ variables: { token, sig, e } });
  }, [token, sig, e, load]);

  const res = data?.orderByLink;
  const list = res?.orders || [];
  const cl = res?.checklist;

  /** Every required row, ticked or not — the same shape as the Messenger card. */
  const rows = cl
    ? [
        ["phone", cl.phone],
        ["address", cl.address],
        ["carrier", cl.carrier],
        ...(cl.paymentRequired ? [["payment", cl.paid ? "ຈ່າຍແລ້ວ" : null]] : []),
      ]
    : [];

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.brand}>4B</div>

        {loading && <p style={S.muted}>ກຳລັງໂຫຼດ…</p>}

        {!loading && (!token || !sig || !e) && (
          <p style={S.err}>{REASON_TEXT.MALFORMED}</p>
        )}

        {!loading && error && (
          <p style={S.err}>ເຊື່ອມຕໍ່ບໍ່ໄດ້ ກະລຸນາລອງໃໝ່</p>
        )}

        {!loading && res && res.status === false && (
          <p style={S.err}>{REASON_TEXT[res.reason] || REASON_TEXT.NOT_FOUND}</p>
        )}

        {!loading && res && res.status === true && (
          <>
            <h1 style={S.h1}>{cl?.complete ? "ຂໍ້ມູນຈັດສົ່ງຄົບແລ້ວ 🎉" : "ອໍເດີ້ຂອງທ່ານ"}</h1>
            <p style={S.muted}>
              {res.shopName}
              {res.buyerName ? ` · ${res.buyerName}` : ""}
            </p>

            <div style={S.block}>
              {rows.map(([key, value]) => (
                <div key={key} style={S.row}>
                  <span style={S.tick}>{value ? "✅" : "⬜️"}</span>
                  <span style={S.rowLabel}>{FIELD_LABEL[key]}</span>
                  <span style={value ? S.rowValue : S.rowMissing}>
                    {value || "ຍັງບໍ່ທັນມີ"}
                  </span>
                </div>
              ))}
            </div>

            {!cl?.complete && (
              <p style={S.note}>
                ກະລຸນາກັບໄປທີ່ Messenger ແລ້ວສົ່ງ
                {" "}
                {(cl?.missing || []).map((m) => FIELD_LABEL[m] || m).join(" · ")}
                {" "}ມາໃຫ້ແດ່
              </p>
            )}

            {list.map((o) => (
              <div key={o.code} style={S.order}>
                <div style={S.orderHead}>
                  <strong>{o.code}</strong>
                  <span>{money(o.totalPrice, o.currency)}</span>
                </div>
                {(o.items || []).map((it, i) => (
                  <div key={i} style={S.item}>
                    <span>{it.name}</span>
                    <span style={S.qty}>x{it.qty}</span>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

//* Inline, so the page has no stylesheet dependency and renders on a cold cache in one trip.
const S = {
  page: { minHeight: "100vh", background: "#f3f4f6", padding: 16, fontFamily: "inherit" },
  card: { maxWidth: 480, margin: "0 auto", background: "#fff", borderRadius: 16, padding: 20 },
  brand: {
    width: 44, height: 44, borderRadius: 12, background: "#4f46e5", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, margin: "0 auto 12px",
  },
  h1: { fontSize: 20, textAlign: "center", margin: "0 0 4px" },
  muted: { textAlign: "center", color: "#6b7280", margin: "0 0 16px" },
  err: { textAlign: "center", color: "#b91c1c", padding: "24px 8px", lineHeight: 1.7 },
  block: { background: "#f9fafb", borderRadius: 12, padding: 12, marginBottom: 12 },
  row: { display: "flex", alignItems: "center", gap: 8, padding: "6px 0" },
  tick: { width: 22 },
  rowLabel: { width: 72, color: "#6b7280" },
  rowValue: { flex: 1, wordBreak: "break-word" },
  rowMissing: { flex: 1, color: "#b91c1c" },
  note: { background: "#fef2f2", color: "#991b1b", borderRadius: 12, padding: 12, lineHeight: 1.7 },
  order: { border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, marginTop: 12 },
  orderHead: { display: "flex", justifyContent: "space-between", marginBottom: 8 },
  item: { display: "flex", justifyContent: "space-between", color: "#374151", padding: "2px 0" },
  qty: { color: "#6b7280" },
};
